import { useRef, useState } from "react";
import type { CustomFunctionUiActionExecutionResult } from "src/api/generated/erp/customFunctions/triggers/uiAction/customFunctionUiActionExecutionResult.ts";
import { CustomFunctionUiActionApi } from "src/api/generated/erp/customFunctions/triggers/uiAction/api/customFunctionUiActionApi.ts";
import { UserInputRequestCard } from "src/components/common/userInputRequests/UserInputRequestCard.tsx";
import { useCloseCallbackForActiveDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { normalizeUserInputRequestPresentation } from "src/components/common/userInputRequests/userInputRequestPresentation.ts";
import {
	type JsonObject,
	toJsonObject,
} from "src/components/common/forms/jsonSchema/jsonSchemaObjectInputUtils.ts";
import type { CloseDialogFunc } from "src/components/common/dialogs/GenericDialogContext.ts";

interface CustomFunctionUiActionContinuationProps {
	initialResult: CustomFunctionUiActionExecutionResult;
	closeDialog: CloseDialogFunc;
	onFinished: (result: CustomFunctionUiActionExecutionResult) => Promise<void>;
}

export function CustomFunctionUiActionContinuation({
	initialResult,
	closeDialog,
	onFinished,
}: CustomFunctionUiActionContinuationProps) {
	const [result, setResult] = useState(initialResult);
	const idempotencyRef = useRef<{ requestId: number; key: string } | null>(null);
	const pendingRequestIdRef = useRef(requireInputRequest(initialResult).id);
	const finishedRef = useRef(false);
	useCloseCallbackForActiveDialog(async () => {
		if (finishedRef.current) return;
		await CustomFunctionUiActionApi.cancelUiActionInputRequest({
			userInputRequestId: pendingRequestIdRef.current,
		});
	});
	const request = result.inputRequest;
	if (request == null) return null;
	const currentRequest = request;
	if (idempotencyRef.current?.requestId !== currentRequest.id) {
		idempotencyRef.current = { requestId: currentRequest.id, key: crypto.randomUUID() };
	}

	return (
		<UserInputRequestCard
			userInputRequestId={currentRequest.id}
			prompt={currentRequest.prompt}
			responseSchema={currentRequest.responseSchema}
			presentation={normalizeUserInputRequestPresentation(currentRequest.presentation)}
			response={currentRequest.response == null ? undefined : toJsonObject(currentRequest.response)}
			expiresAt={currentRequest.expiresAt}
			disabled={currentRequest.status !== "PENDING"}
			displayMode="dialog"
			onCancel={closeDialog}
			onRespond={respond}
		/>
	);

	async function respond(response: JsonObject) {
		const idempotency = idempotencyRef.current;
		if (idempotency == null || idempotency.requestId !== currentRequest.id) return;
		const nextResult = await CustomFunctionUiActionApi.resumeUiAction({
			userInputRequestId: currentRequest.id,
			response,
			idempotencyKey: idempotency.key,
		});
		if (nextResult.inputRequest != null) {
			pendingRequestIdRef.current = nextResult.inputRequest.id;
			setResult(nextResult);
			return;
		}
		finishedRef.current = true;
		await onFinished(nextResult);
	}
}

function requireInputRequest(result: CustomFunctionUiActionExecutionResult) {
	if (result.inputRequest == null) {
		throw new Error("A Custom Function continuation requires an input request.");
	}
	return result.inputRequest;
}
